import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isStrongPassword, PASSWORD_RULE_MSG } from '../lib/passwordPolicy';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Building2, Download, Eye, EyeOff, FileCheck2, Upload, ShieldCheck, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIZE = 10 * 1024 * 1024;

const TEXT_FIELDS = [
  { section: 'Firma Bilgileri', key: 'trade_name', label: 'Firmanın Ticari Unvanı', placeholder: 'ör. ABC İnşaat Taahhüt Ltd. Şti.', full: true },
  { section: 'Firma Bilgileri', key: 'company_name', label: 'Firmanın Adı', placeholder: 'ör. ABC İnşaat' },
  { section: 'Firma Bilgileri', key: 'sgk_sicil_no', label: 'SGK Sicil Numarası', placeholder: 'İşyeri SGK sicil no' },
  { section: 'Firma Bilgileri', key: 'nace_kodu', label: 'NACE Kodu', placeholder: 'ör. 43.21.01' },
  { section: 'Firma Bilgileri', key: 'activity_description', label: 'Faaliyet / İş Tanımı', placeholder: 'ör. Elektrik tesisatı, pano bakım ve onarımı, aydınlatma montajı', full: true },
  { section: 'Kayıt Eden Yetkili', key: 'first_name', label: 'Yetkilinin Adı', placeholder: 'Adı' },
  { section: 'Kayıt Eden Yetkili', key: 'last_name', label: 'Yetkilinin Soyadı', placeholder: 'Soyadı' },
  { section: 'Kayıt Eden Yetkili', key: 'tc_kimlik_no', label: 'T.C. Kimlik No', placeholder: '11 haneli', inputMode: 'numeric', maxLength: 11 },
  { section: 'Kayıt Eden Yetkili', key: 'phone', label: 'Telefon', placeholder: '05xx xxx xx xx', type: 'tel' },
  { section: 'Giriş Bilgileri', key: 'email', label: 'E-posta', placeholder: 'ornek@firma.com', type: 'email' },
];
const FILE_FIELDS = [
  { key: 'kvkk_taahhutnamesi', label: 'KVKK Taahhütnamesi', template: 'kvkk-template' },
  { key: 'aydinlatma_yukumlulugu', label: 'Aydınlatma Yükümlülüğü', template: 'aydinlatma-template' },
  { key: 'vergi_levhasi', label: 'Vergi Levhası' },
];
const SECTIONS = ['Firma Bilgileri', 'Kayıt Eden Yetkili', 'Giriş Bilgileri'];

const validate = (values, files, consent) => {
  const e = {};
  TEXT_FIELDS.forEach(f => { if (!values[f.key]?.trim()) e[f.key] = `${f.label} zorunludur`; });
  if (values.tc_kimlik_no && !/^\d{11}$/.test(values.tc_kimlik_no)) e.tc_kimlik_no = 'T.C. Kimlik No 11 haneli rakam olmalıdır';
  if (values.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)) e.email = 'Geçerli bir e-posta girin';
  if (values.phone && values.phone.replace(/\D/g, '').length < 10) e.phone = 'Geçerli bir telefon girin';
  if (!values.password) e.password = 'Şifre zorunludur';
  else if (!isStrongPassword(values.password)) e.password = PASSWORD_RULE_MSG;
  if (values.password && values.password !== values.password2) e.password2 = 'Şifreler eşleşmiyor';
  FILE_FIELDS.forEach(f => { if (!files[f.key]) e[f.key] = `${f.label} yüklemeniz gerekir`; });
  if (!consent) e.consent = 'Aydınlatma Metni\'ni okuyup "Okudum ve anladım" onayını vermeniz zorunludur';
  return e;
};

const FieldError = ({ msg, id }) => (msg ? <p className="text-xs text-rose-600 mt-1" data-testid={`register-error-${id}`}>{msg}</p> : null);

const FileField = ({ field, file, error, onChange, template }) => (
  <div>
    <div className="flex items-center justify-between gap-2">
      <Label>{field.label} <span className="text-rose-500">*</span></Label>
      {template && (
        <a href={`${API}/public/${field.template}`} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline" data-testid={`register-template-download-${field.key}`}>
          <Download className="h-3.5 w-3.5" />Şablonu İndir
        </a>
      )}
    </div>
    <label
      className={`mt-1.5 flex items-center gap-3 rounded-lg border-2 border-dashed px-4 py-3 cursor-pointer transition-colors ${error ? 'border-rose-400 bg-rose-50' : file ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-400 bg-white'}`}
      data-testid={`register-file-${field.key}`}
    >
      {file ? <FileCheck2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <Upload className="h-5 w-5 text-slate-400 shrink-0" />}
      <span className={`text-sm truncate ${file ? 'text-emerald-800' : 'text-slate-500'}`}>{file ? file.name : 'PDF veya resim seçin (en fazla 10 MB)'}</span>
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={onChange} data-testid={`register-file-input-${field.key}`} />
    </label>
    <FieldError msg={error} id={field.key} />
  </div>
);

import { useEmailCheck, EmailCheckHint } from '../components/EmailCheckHint';

export default function CompanyRegisterPage() {
  const { registerCompany } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState({});
  const emailCheck = useEmailCheck(values.email, { isPublic: true });
  const [files, setFiles] = useState({});
  const [consent, setConsent] = useState(false);
  const [hasReadText, setHasReadText] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [consentText, setConsentText] = useState('');
  const [templates, setTemplates] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/aydinlatma-metni`).then(res => setConsentText(res.data.text)).catch(() => setConsentText(''));
    ['kvkk-template', 'aydinlatma-template'].forEach(t => axios.get(`${API}/public/${t}/info`).then(res => setTemplates(p => ({ ...p, [t]: res.data.available }))).catch(() => {}));
  }, []);

  useEffect(() => { if (submitted) setErrors(validate(values, files, consent)); }, [values, files, consent, submitted]);

  const setValue = (key, v) => setValues(prev => ({ ...prev, [key]: key === 'tc_kimlik_no' ? v.replace(/\D/g, '') : v }));
  const setFile = (key) => (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > MAX_SIZE) { toast.error("Dosya boyutu 10 MB'ı aşamaz"); return; }
    setFiles(prev => ({ ...prev, [key]: f }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate(values, files, consent);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error('Lütfen kırmızı ile işaretli eksik alanları tamamlayın');
      document.querySelector(`[data-field="${Object.keys(errs)[0]}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const body = new FormData();
    TEXT_FIELDS.forEach(f => body.append(f.key, values[f.key].trim()));
    body.append('password', values.password);
    body.append('consent_accepted', 'true');
    FILE_FIELDS.forEach(f => body.append(f.key, files[f.key]));
    setSaving(true);
    try {
      await registerCompany(body);
      toast.success('Kaydınız alındı');
      navigate('/');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Kayıt tamamlanamadı, lütfen bilgileri kontrol edin');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = (key) => (errors[key] ? 'border-rose-500 focus-visible:ring-rose-300' : '');

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="register-back-to-login">
          <ArrowLeft className="h-4 w-4" /> Giriş ekranına dön
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-orange-500 flex items-center justify-center"><Building2 className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Firma Kaydı</h1>
            <p className="text-slate-500 text-sm">Bilgileriniz İSG biriminin onayına gönderilecektir.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-6" data-testid="company-register-form">
          {SECTIONS.map(section => (
            <section key={section} className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-base font-semibold text-slate-800 mb-4">{section}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {TEXT_FIELDS.filter(f => f.section === section).map(f => (
                  <div key={f.key} className={f.full ? 'sm:col-span-2' : ''} data-field={f.key}>
                    <Label htmlFor={f.key}>{f.label} <span className="text-rose-500">*</span></Label>
                    <Input
                      id={f.key} type={f.type || 'text'} inputMode={f.inputMode} maxLength={f.maxLength}
                      value={values[f.key] || ''} onChange={(e) => setValue(f.key, e.target.value)}
                      placeholder={f.placeholder} className={`mt-1.5 ${inputCls(f.key)}`} data-testid={`register-${f.key}`}
                    />
                    <FieldError msg={errors[f.key]} id={f.key} />
                    {f.key === 'email' && <EmailCheckHint state={emailCheck} testId="register-email-check" />}
                  </div>
                ))}
                {section === 'Giriş Bilgileri' && (
                  <>
                    <div data-field="password">
                      <Label htmlFor="password">Kullanıcı Şifresi <span className="text-rose-500">*</span></Label>
                      <div className="relative mt-1.5">
                        <Input id="password" type={showPassword ? 'text' : 'password'} value={values.password || ''} onChange={(e) => setValue('password', e.target.value)} placeholder="En az 8 karakter, harf ve rakam" className={inputCls('password')} data-testid="register-password" />
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPassword(!showPassword)} aria-label="Şifreyi göster">
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <FieldError msg={errors.password} id="password" />
                    </div>
                    <div data-field="password2">
                      <Label htmlFor="password2">Şifre Tekrar <span className="text-rose-500">*</span></Label>
                      <Input id="password2" type={showPassword ? 'text' : 'password'} value={values.password2 || ''} onChange={(e) => setValue('password2', e.target.value)} className={`mt-1.5 ${inputCls('password2')}`} data-testid="register-password2" />
                      <FieldError msg={errors.password2} id="password2" />
                    </div>
                  </>
                )}
              </div>
            </section>
          ))}

          <section className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-base font-semibold text-slate-800 mb-4">Belgeler</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {FILE_FIELDS.map(f => (
                <div key={f.key} data-field={f.key}><FileField field={f} file={files[f.key]} error={errors[f.key]} onChange={setFile(f.key)} template={f.template && templates[f.template]} /></div>
              ))}
            </div>
          </section>

          <section className={`rounded-xl border p-6 ${errors.consent ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-white'}`} data-field="consent">
            <div className="flex items-start gap-3">
              <ShieldCheck className={`h-5 w-5 mt-0.5 ${consent ? 'text-emerald-600' : 'text-slate-400'}`} />
              <div className="flex-1 space-y-3">
                <button type="button" onClick={() => setTextOpen(true)} className="font-medium text-blue-700 hover:underline text-left" data-testid="register-open-consent-text">
                  İSG PORTAL Aydınlatma Metni
                </button>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="consent" checked={consent} disabled={!hasReadText}
                    onCheckedChange={(c) => setConsent(!!c)} data-testid="register-consent-checkbox"
                  />
                  <label htmlFor="consent" className={`text-sm ${hasReadText ? 'cursor-pointer text-slate-700' : 'text-slate-400'}`}>
                    Okudum ve anladım <span className="text-rose-500">*</span>
                  </label>
                </div>
                {!hasReadText && <p className="text-xs text-slate-500">Onay verebilmek için önce metni açıp okuyun.</p>}
                <FieldError msg={errors.consent} id="consent" />
              </div>
            </div>
          </section>

          <Button type="submit" disabled={saving || emailCheck.status === 'taken'} className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-base" data-testid="register-submit-btn">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Kaydediliyor...</> : 'Kaydı Tamamla'}
          </Button>
        </form>
      </div>

      <Dialog open={textOpen} onOpenChange={setTextOpen}>
        <DialogContent className="max-w-2xl" data-testid="consent-text-dialog">
          <DialogHeader><DialogTitle>İSG PORTAL Aydınlatma Metni</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto whitespace-pre-line text-sm text-slate-700 leading-relaxed pr-2">{consentText}</div>
          <DialogFooter>
            <Button
              onClick={() => { setHasReadText(true); setConsent(true); setTextOpen(false); }}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="consent-accept-btn"
            >
              Okudum ve anladım
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
